import React from "react";

export type ShadowV1 = {
  id: string;
  dx: number;
  dy: number;
  shadowBlur: number;
  color: string;
  opacity: number;
  isHidden: boolean;
};

export type ShadowV2 = {
  id: string;
  version: 2;
  type: "drop" | "inner";
  x: number;
  y: number;
  blur: number;
  spread: number;
  color: string;
  opacity: number;
  isHidden: boolean;
  // Legacy v1 mirrors — written for tldraw schema and any code path still reading old field names
  dx: number;
  dy: number;
  shadowBlur: number;
};

export type ShadowAny =
  | ShadowV1
  | ShadowV2
  | (Partial<ShadowV1> & Partial<ShadowV2> & { id: string; isHidden?: boolean });

export type ShadowMap = Record<string, ShadowAny>;

const isV2 = (s: ShadowAny): s is ShadowV2 => (s as ShadowV2).version === 2;

const splitHexAlpha = (hex: string): { rgb: string; alpha: number } => {
  if (!hex || typeof hex !== "string") return { rgb: "#000000", alpha: 1 };
  const h = hex.trim();
  if (/^#?[0-9a-f]{8}$/i.test(h)) {
    const clean = h.startsWith("#") ? h.slice(1) : h;
    const rgb = `#${clean.slice(0, 6)}`;
    const alpha = parseInt(clean.slice(6, 8), 16) / 255;
    return { rgb, alpha };
  }
  if (/^#?[0-9a-f]{6}$/i.test(h)) {
    return { rgb: h.startsWith("#") ? h : `#${h}`, alpha: 1 };
  }
  if (/^#?[0-9a-f]{3}$/i.test(h)) {
    const clean = h.startsWith("#") ? h.slice(1) : h;
    return { rgb: `#${clean[0]}${clean[0]}${clean[1]}${clean[1]}${clean[2]}${clean[2]}`, alpha: 1 };
  }
  return { rgb: "#000000", alpha: 1 };
};

export const normalizeShadow = (raw: ShadowAny, type: "drop" | "inner"): ShadowV2 => {
  if (isV2(raw)) {
    const x = raw.x ?? (raw as any).dx ?? 0;
    const y = raw.y ?? (raw as any).dy ?? 0;
    const blur = raw.blur ?? (raw as any).shadowBlur ?? 0;
    return {
      ...raw,
      type: raw.type ?? type,
      x,
      y,
      blur,
      spread: raw.spread ?? 0,
      dx: x,
      dy: y,
      shadowBlur: blur,
    };
  }

  const v1 = raw as Partial<ShadowV1> & { id: string; isHidden?: boolean };
  const { rgb, alpha } = splitHexAlpha(v1.color ?? "#000000");
  const v1Opacity = typeof v1.opacity === "number" ? v1.opacity : 1;
  const combinedOpacity = Math.round(alpha * v1Opacity * 100);
  const x = v1.dx ?? 0;
  const y = v1.dy ?? 0;
  const blur = v1.shadowBlur ?? 0;

  return {
    id: v1.id,
    version: 2,
    type,
    x,
    y,
    blur,
    spread: 0,
    color: rgb,
    opacity: Math.max(0, Math.min(100, combinedOpacity)),
    isHidden: v1.isHidden ?? false,
    dx: x,
    dy: y,
    shadowBlur: blur,
  };
};

export const normalizeShadowMap = (map: ShadowMap | undefined, type: "drop" | "inner"): ShadowV2[] => {
  if (!map) return [];
  return Object.values(map).map((s) => normalizeShadow(s, type));
};

export const createDefaultShadowV2 = (type: "drop" | "inner"): ShadowV2 => {
  // Default shadow knobs — kept tight on purpose so freshly added
  // shadows read as a subtle lift rather than a heavy halo. Users
  // can crank any of these via the popover.
  //   x:0, y:4, blur:4, spread:0, opacity:25% (drop)
  // Inner shadows mirror Y to -4 so they read as light "from above"
  // and use a higher opacity (40%) so the inverted glow is visible
  // against the shape's fill.
  //
  // We encode the same opacity into the 8-digit hex AS WELL as
  // setting it on `opacity` because the swatch in
  // `ColorPickerPopover` reads opacity from the hex's alpha byte
  // (`getOpacityPercentage(color)`), while the renderer uses
  // `s.opacity` from the shadow record. Without the alpha byte the
  // picker would show "100%" on a freshly added shadow even though
  // the rendered shadow correctly draws at 25%.
  //   25%  → round(0.25 * 255) = 64  = 0x40
  //   40%  → round(0.40 * 255) = 102 = 0x66
  const x = 0;
  const y = type === "drop" ? 4 : -4;
  const blur = 4;
  const opacityPct = type === "drop" ? 25 : 40;
  const alphaHex = type === "drop" ? "40" : "66";
  return {
    id: Date.now().toString(),
    version: 2,
    type,
    x,
    y,
    blur,
    spread: 0,
    color: `#000000${alphaHex}`,
    opacity: opacityPct,
    isHidden: false,
    dx: x,
    dy: y,
    shadowBlur: blur,
  };
};

/**
 * Effective shadow alpha (0..1) for SVG filter rendering.
 *
 * A shadow has two potential opacity sources that historically the
 * renderer multiplied together — which produced the bug "I set 100%
 * in the color picker but the shadow is still 25% transparent":
 *
 *   1. Alpha byte encoded into `color` as an 8-digit hex (the field the
 *      color picker writes to via `setOpacityPercentage`)
 *   2. The numeric `opacity` field on the shadow record (kept around
 *      for v1 compatibility and seeded by `createDefaultShadowV2`)
 *
 * Multiplying them is wrong because the picker only updates (1) — so
 * even a fully-opaque pick (alpha=FF) gets attenuated by whatever
 * legacy value (2) holds (defaults to 25 for drop shadows).
 *
 * Resolution: treat (1) as authoritative when present (i.e. color is
 * an 8-digit hex), and fall back to (2) only for shadows whose color
 * is a plain 6-digit RGB string (typical of v1 records after
 * normalization, which strips alpha into the opacity field). This
 * matches what users see in the picker UI and what `shadowRgba` does
 * for the CSS-preview path.
 */
const getEffectiveShadowAlpha = (s: ShadowV2): number => {
  const hex = (s.color || "").trim();
  const explicit = /^#?[0-9a-f]{8}$/i.test(hex);
  if (explicit) {
    const clean = hex.startsWith("#") ? hex.slice(1) : hex;
    return parseInt(clean.slice(6, 8), 16) / 255;
  }
  return Math.max(0, Math.min(1, (s.opacity ?? 0) / 100));
};

const getShadowRgb = (s: ShadowV2): string => splitHexAlpha(s.color).rgb;

type FilterDefsProps = {
  outShadowArray?: ShadowMap;
  inShadowArray?: ShadowMap;
  /** Shape width in user-space px. When provided, filter regions are
   *  sized to the actual blur/offset/spread so large blurs don't get
   *  clipped to a square at the legacy 200% region. Optional for
   *  back-compat (falls back to 200%). */
  shapeWidth?: number;
  /** Shape height in user-space px. See `shapeWidth`. */
  shapeHeight?: number;
};

/**
 * Compute the SVG `<filter>` region attributes needed so a Gaussian
 * blur (and its offset/spread) doesn't get clipped to a rectangle.
 *
 * Gaussian blur visually fades to ~0.3% intensity by 3σ; offsets push
 * the visible shadow further in one direction; `spread` (feMorphology)
 * extends symmetrically. We compute the exact pixel margins each side
 * needs and emit `filterUnits="userSpaceOnUse"` with absolute pixel
 * coordinates — percentages relative to `objectBoundingBox` are
 * percentage-rounded and can leave 1–2px hairline cutoffs on the
 * right/bottom edges (which is exactly the symptom users see at
 * blur=0: a tiny clip on the right that disappears once the blur
 * fades the edge below perceptible alpha).
 *
 * Generous baseline margin (`MIN_MARGIN_PX`) prevents regressions for
 * shadows where the math comes out to ~0 (e.g. no blur, no spread,
 * no offset): SVG renderers can still leave faint anti-aliasing or
 * stroke-extension artifacts a pixel or two outside the path, so we
 * always give them at least this much breathing room.
 *
 * Falls back to the legacy percentage form (`200%` region, default
 * filterUnits) if shape dimensions aren't provided — keeps callers
 * that haven't been wired through working without changes.
 */
export const buildFilterRegion = (
  params: { blur: number; x?: number; y?: number; spread?: number },
  shapeWidth?: number,
  shapeHeight?: number
): { filterUnits?: "userSpaceOnUse" | "objectBoundingBox"; x: string; y: string; width: string; height: string } => {
  const { blur, x = 0, y = 0, spread = 0 } = params;
  if (!shapeWidth || !shapeHeight || shapeWidth <= 0 || shapeHeight <= 0) {
    return { x: "-50%", y: "-50%", width: "200%", height: "200%" };
  }
  // Safety pad ADDED on top of the computed shadow extent (additive,
  // not floor-via-max). With a floor, a shadow with y=4 and blur=0
  // produces bottom-margin = max(PAD, 4) = 4, which is EXACTLY the
  // shadow's reach — leaving the bottom edge at the filter region's
  // boundary and producing a hairline clip. Adding the pad on top
  // gives every shadow a guaranteed `PAD` pixels of breathing room
  // past its furthest visible edge, eliminating both the "no blur
  // tiny right-edge clip" symptom AND any anti-aliasing artifacts at
  // the path's geometric edge.
  const PAD = 4;
  const sigma3 = Math.ceil(3 * Math.max(0, blur));
  const sp = Math.ceil(Math.abs(spread));
  const left = sigma3 + sp + Math.max(0, -x) + PAD;
  const right = sigma3 + sp + Math.max(0, x) + PAD;
  const top = sigma3 + sp + Math.max(0, -y) + PAD;
  const bottom = sigma3 + sp + Math.max(0, y) + PAD;
  return {
    // userSpaceOnUse means the x/y/width/height below are absolute
    // values in the current SVG user coordinate space (i.e. raw px in
    // our shape's viewBox). Bypasses the percentage-of-bbox rounding
    // that introduced the right-edge hairline clip.
    filterUnits: "userSpaceOnUse",
    x: `${-left}`,
    y: `${-top}`,
    width: `${shapeWidth + left + right}`,
    height: `${shapeHeight + top + bottom}`,
  };
};

const OuterShadowFilter = ({
  shadow,
  shapeWidth,
  shapeHeight,
}: {
  shadow: ShadowV2;
  shapeWidth?: number;
  shapeHeight?: number;
}) => {
  const hasSpread = shadow.spread !== 0;
  const morphology = hasSpread ? (
    shadow.spread > 0 ? (
      <feMorphology in="SourceAlpha" operator="dilate" radius={shadow.spread} result="spread" />
    ) : (
      <feMorphology in="SourceAlpha" operator="erode" radius={Math.abs(shadow.spread)} result="spread" />
    )
  ) : null;
  const blurInput = hasSpread ? "spread" : "SourceAlpha";
  const region = buildFilterRegion(
    { blur: shadow.blur, x: shadow.x, y: shadow.y, spread: shadow.spread },
    shapeWidth,
    shapeHeight
  );

  // RGB-only color + a separate explicit floodOpacity — avoids the
  // historical bug where `color` carrying an 8-digit hex alpha got
  // multiplied with `shadow.opacity/100`, attenuating fully-opaque
  // picker selections down to whatever the legacy `opacity` field was.
  // See `getEffectiveShadowAlpha` for the full rationale.
  const floodRgb = getShadowRgb(shadow);
  const floodAlpha = getEffectiveShadowAlpha(shadow);
  return (
    <filter id={shadow.id} {...region}>
      {morphology}
      <feGaussianBlur in={blurInput} stdDeviation={shadow.blur} />
      <feOffset dx={shadow.x} dy={shadow.y} result="offsetBlur" />
      <feFlood floodColor={floodRgb} floodOpacity={floodAlpha} />
      <feComposite in2="offsetBlur" operator="in" />
      <feMerge>
        <feMergeNode />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  );
};

const InnerShadowFilter = ({
  shadow,
  shapeWidth,
  shapeHeight,
}: {
  shadow: ShadowV2;
  shapeWidth?: number;
  shapeHeight?: number;
}) => {
  const hasSpread = shadow.spread !== 0;
  const morphology = hasSpread ? (
    shadow.spread > 0 ? (
      <feMorphology in="SourceAlpha" operator="erode" radius={shadow.spread} result="spread" />
    ) : (
      <feMorphology in="SourceAlpha" operator="dilate" radius={Math.abs(shadow.spread)} result="spread" />
    )
  ) : null;
  const inputForInvert = hasSpread ? "spread" : "SourceAlpha";
  const region = buildFilterRegion(
    { blur: shadow.blur, x: shadow.x, y: shadow.y, spread: shadow.spread },
    shapeWidth,
    shapeHeight
  );

  // Same de-double-counting fix as OuterShadowFilter: feed feFlood
  // with RGB-only and apply the effective alpha via the existing
  // feComponentTransfer slope step. Avoids picker-alpha × opacity
  // double-multiplication.
  const floodRgb = getShadowRgb(shadow);
  const floodAlpha = getEffectiveShadowAlpha(shadow);
  return (
    <filter id={shadow.id} {...region}>
      {morphology}
      <feComponentTransfer in={inputForInvert} result="inverted">
        <feFuncA type="table" tableValues="1 0" />
      </feComponentTransfer>
      <feGaussianBlur in="inverted" stdDeviation={shadow.blur} />
      <feOffset dx={shadow.x} dy={shadow.y} result="offsetBlur" />
      <feFlood floodColor={floodRgb} result="color" />
      <feComposite in="color" in2="offsetBlur" operator="in" />
      <feComponentTransfer>
        <feFuncA type="linear" slope={floodAlpha} />
      </feComponentTransfer>
      <feComposite in2="SourceAlpha" operator="in" />
      <feComposite operator="over" in2="SourceGraphic" />
    </filter>
  );
};

export const ShapeShadowDefs: React.FC<FilterDefsProps> = ({
  outShadowArray,
  inShadowArray,
  shapeWidth,
  shapeHeight,
}) => {
  const outs = normalizeShadowMap(outShadowArray, "drop").filter((s) => !s.isHidden);
  const ins = normalizeShadowMap(inShadowArray, "inner").filter((s) => !s.isHidden);
  return (
    <>
      {outs.map((s) => (
        <OuterShadowFilter key={s.id} shadow={s} shapeWidth={shapeWidth} shapeHeight={shapeHeight} />
      ))}
      {ins.map((s) => (
        <InnerShadowFilter key={s.id} shadow={s} shapeWidth={shapeWidth} shapeHeight={shapeHeight} />
      ))}
    </>
  );
};

const shadowRgba = (s: ShadowV2): string => {
  const { rgb } = splitHexAlpha(s.color);
  const r = parseInt(rgb.slice(1, 3), 16);
  const g = parseInt(rgb.slice(3, 5), 16);
  const b = parseInt(rgb.slice(5, 7), 16);
  // Use the same alpha resolution as the SVG filter — picker-set alpha
  // (when present) is authoritative, fall back to the numeric opacity
  // field for v1-normalized shadows. Keeps CSS box-shadow / text-shadow
  // previews aligned with the canvas-rendered SVG filter.
  const a = getEffectiveShadowAlpha(s).toFixed(3);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const shadowsToCssBoxShadow = (
  outShadowArray: ShadowMap | undefined,
  inShadowArray: ShadowMap | undefined
): string => {
  const outs = normalizeShadowMap(outShadowArray, "drop").filter((s) => !s.isHidden);
  const ins = normalizeShadowMap(inShadowArray, "inner").filter((s) => !s.isHidden);
  const parts = [
    ...outs.map((s) => `${s.x}px ${s.y}px ${s.blur}px ${s.spread}px ${shadowRgba(s)}`),
    ...ins.map((s) => `inset ${s.x}px ${s.y}px ${s.blur}px ${s.spread}px ${shadowRgba(s)}`),
  ];
  return parts.join(", ");
};

/**
 * Compose a CSS `filter` value as a chain of `drop-shadow(...)` from visible drop shadows.
 * Used for elements where CSS `filter: drop-shadow(...)` is preferable to inline SVG filters
 * (e.g. icons, where wrappers with `overflow: hidden` would otherwise clip an SVG-filter shadow).
 *
 * Note: CSS `drop-shadow()` does NOT support spread — the spread value is ignored here.
 */
export const shadowsToCssFilterDropShadow = (outShadowArray: ShadowMap | undefined): string => {
  const outs = normalizeShadowMap(outShadowArray, "drop").filter((s) => !s.isHidden);
  if (outs.length === 0) return "";
  return outs.map((s) => `drop-shadow(${s.x}px ${s.y}px ${s.blur}px ${shadowRgba(s)})`).join(" ");
};

export const shadowToCssTextShadow = (outShadowArray: ShadowMap | undefined): string => {
  const outs = normalizeShadowMap(outShadowArray, "drop").filter((s) => !s.isHidden);
  if (outs.length === 0) return "";
  return outs
    .map((s) => {
      // Same alpha resolution as the SVG filter / box-shadow path —
      // picker-set hex alpha is authoritative when present.
      const a = getEffectiveShadowAlpha(s).toFixed(3);
      const { rgb } = splitHexAlpha(s.color);
      const r = parseInt(rgb.slice(1, 3), 16);
      const g = parseInt(rgb.slice(3, 5), 16);
      const b = parseInt(rgb.slice(5, 7), 16);
      return `${s.x}px ${s.y}px ${s.blur}px rgba(${r}, ${g}, ${b}, ${a})`;
    })
    .join(", ");
};
